import { api } from './api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface Group {
  id: string;
  reference_id: string;
  name: string;
  description: string | null;
  category: string;
  start_date: string | null;
  end_date: string | null;
  default_team_id: string | null;
  is_active: boolean;
  owner_id: string;
  owner?: { id: string; full_name: string; avatar_url: string | null } | null;
  members_count?: number;
  goals_count?: number;
  sprints_count?: number;
  created_at: string;
  updated_at: string;
}

export interface GroupDetail extends Group {
  owner: { id: string; full_name: string; avatar_url: string | null };
  stats: { goals_count: number; sprints_count: number; members_count: number };
  current_user_role: 'owner' | 'moderator' | 'member' | null;
}

export interface GroupMember {
  id: string;
  user: { id: string; full_name: string; email: string; avatar_url: string | null };
  role: string;
  created_at: string;
}

export interface Pagination {
  total_count: number;
  has_more: boolean;
  cursor: string | null;
  limit: number;
}

export interface GroupListParams {
  cursor?: string;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  category?: string;
  owner_id?: string;
}

export interface MemberListParams {
  cursor?: string;
  limit?: number;
  search?: string;
  role?: string;
}

export interface WorkspaceUser {
  id: string;
  full_name: string;
  email: string;
  avatar_url: string | null;
}

export interface CreateGroupData {
  name: string;
  description?: string;
  category?: string;
  start_date?: string;
  end_date?: string;
  default_team_id?: string;
  enable_project_team?: boolean;
  owner_id?: string;
}

export interface UpdateGroupData {
  name?: string;
  description?: string | null;
  category?: string;
  start_date?: string | null;
  end_date?: string | null;
  default_team_id?: string | null;
  is_active?: boolean;
}

export interface AddMemberData {
  user_id?: string;
  email?: string;
  role?: string;
}

export const GROUP_CATEGORIES = [
  'project', 'product', 'team', 'period', 'company',
  'department', 'initiative', 'other', 'release', 'feature', 'cr', 'sla',
] as const;

export const GROUP_MEMBER_ROLES = ['owner', 'moderator', 'member'] as const;

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------
export const groupService = {
  list: (params?: GroupListParams) =>
    api.get<{ success: boolean; data: Group[]; pagination: Pagination }>('/groups', { params }),

  get: (id: string) =>
    api.get<{ success: boolean; data: GroupDetail }>(`/groups/${id}`),

  create: (data: CreateGroupData) =>
    api.post<{ success: boolean; data: Group }>('/groups', data),

  update: (id: string, data: UpdateGroupData) =>
    api.patch<{ success: boolean; data: Group }>(`/groups/${id}`, data),

  delete: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/groups/${id}`),

  listMembers: (groupId: string, params?: MemberListParams) =>
    api.get<{ success: boolean; data: GroupMember[]; pagination: Pagination }>(
      `/groups/${groupId}/members`, { params },
    ),

  addMember: (groupId: string, data: AddMemberData) =>
    api.post<{ success: boolean; data: GroupMember }>(`/groups/${groupId}/members`, data),

  updateMember: (groupId: string, memberId: string, data: { role: string }) =>
    api.patch<{ success: boolean; data: GroupMember }>(`/groups/${groupId}/members/${memberId}`, data),

  removeMember: (groupId: string, memberId: string) =>
    api.delete<{ success: boolean; message: string }>(`/groups/${groupId}/members/${memberId}`),

  searchWorkspaceMembers: (workspaceId: string, query: string) =>
    api.get<{ success: boolean; data: Array<{ id: string; user: WorkspaceUser; role: string }> }>(
      `/workspaces/${workspaceId}/members`, { params: { search: query, limit: 10 } },
    ),
};
